import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getNoticiaBySlug } from '../../config/services/strapi/noticias';
import './detalle-noticia-fabrica.css';
import TextSkeleton from '../../components/Skeleton/TextSkeleton';
import ImageSkeleton from '../../components/Skeleton/ImageSkeleton';

const DetalleNoticiaFabrica = () => {
  const { slug } = useParams<{ slug: string }>();
  const [noticia, setNoticia] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const sectionRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const shareUrl = typeof window !== 'undefined' ? encodeURIComponent(window.location.href) : '';
  const shareTitle = noticia ? encodeURIComponent(noticia.descripcionImage?.[0]?.children?.[0]?.text || "Noticia") : '';

  useEffect(() => {
    async function loadData() {
      if (slug) {
        const data = await getNoticiaBySlug(slug);
        setNoticia(data);
        setLoading(false);
      }
    }
    loadData();
  }, [slug]);

  useEffect(() => {
    if (loading || !noticia) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          contentRef.current?.classList.add('is-visible');
        } else {
          entry.target.classList.remove('is-visible');
          contentRef.current?.classList.remove('is-visible');
        }
      },
      { threshold: 0.6 }
    );

    const currentSection = sectionRef.current;
    if (currentSection) observer.observe(currentSection);

    return () => {
      if (currentSection) observer.unobserve(currentSection);
    };
  }, [loading, noticia]);

  if (loading) {
    return (
      <div className="loading-state">
        <TextSkeleton />
        <ImageSkeleton className="mt-4 h-64" />
      </div>
    );
  }
  if (!noticia) return <div className="error-state">Noticia no encontrada</div>;

  const tituloPrincipal = noticia.descripcionImage?.[0]?.children?.[0]?.text || "Sin título";
  const fecha = noticia.anoImage;
  const imagenUrl = noticia.image?.url || "";

  return (
    <>
      <section ref={sectionRef} className="header-noticia-simple-fabrica">
        <div ref={contentRef} className="header-noticia-content-fabrica">
          <div className="header-noticia-info-fabrica">
            <span className="header-date-fabrica">{fecha}</span>
            <span className="header-separator-fabrica">|</span>
            <span className="header-category-fabrica">In <Link to="/noticias">Noticias</Link></span>
          </div>
          <h1 className="header-noticia-title-fabrica">
            {tituloPrincipal}
          </h1>
        </div>
        <a href="#proxima-seccion" className="header-scrolldown-fabrica">
          <i className="fa-solid fa-angle-down"></i>
        </a>
      </section>

      <section id="proxima-seccion" className="section-cuerpo-noticia-fabrica">
        <div className="contenedor-lectura-fabrica">
          <div className="contenido-texto-fabrica">

            {noticia.descripcionDetalle?.map((block: any, index: number) => {
              const text = block.children?.map((c: any) => c.text).join("");
              if (!text) return null;

              const blockKey = `detalle-${noticia.id || 'noticia'}-${index}`;

              if (text.startsWith("http")) {
                return (
                  <p key={blockKey} className="enlace-entrevista-fabrica">
                    <a href={text} target="_blank" rel="noopener noreferrer">
                      {text}
                    </a>
                  </p>
                );
              }

              if (index === 0) return <p key={blockKey} className="dato-emision-fabrica">{text}</p>;

              return <p key={blockKey}>{text}</p>;
            })}

            {imagenUrl && (
              <div className="imagen-centrada-fabrica">
                <img src={imagenUrl} alt={tituloPrincipal} />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Footer Social */}
      <section className="section-compartir-footer-fabrica">
        <div className="post-footer-compartir-fabrica">
          <div className="footer-compartir-inner-fabrica">
            <div className="social load">
              <ul>
                {/* Facebook */}
                <li className="social-facebook">
                  <a href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`} target="_blank" rel="noreferrer">
                    <i className="fa-brands fa-facebook-f"></i>
                  </a>
                </li>

                {/* X (Twitter) */}
                <li className="social-twitter">
                  <a href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareTitle}`} target="_blank" rel="noreferrer">
                    <i className="fa-brands fa-x-twitter"></i>
                  </a>
                </li>

                {/* Threads */}
                <li className="social-threads">
                  <a href={`https://www.threads.net/intent/post?text=${shareTitle}%20${shareUrl}`} target="_blank" rel="noreferrer">
                    <i className="fa-brands fa-threads"></i>
                  </a>
                </li>

                {/* Pinterest */}
                <li className="social-pinterest">
                  <a href={`https://pinterest.com/pin/create/button/?url=${shareUrl}&description=${shareTitle}`} target="_blank" rel="noreferrer">
                    <i className="fa-brands fa-pinterest-p"></i>
                  </a>
                </li>

                {/* LinkedIn */}
                <li className="social-linkedin">
                  <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`} target="_blank" rel="noreferrer">
                    <i className="fa-brands fa-linkedin-in"></i>
                  </a>
                </li>

                {/* WhatsApp */}
                <li className="social-whatsapp">
                  <a href={`https://api.whatsapp.com/send?text=${shareTitle}%20${shareUrl}`} target="_blank" rel="noreferrer">
                    <i className="fa-brands fa-whatsapp"></i>
                  </a>
                </li>

                {/* Bluesky */}
                <li className="social-bluesky">
                  <a href={`https://bsky.app/intent/compose?text=${shareTitle}%20${shareUrl}`} target="_blank" rel="noreferrer" className="icon-bluesky">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 10.8c-1.3-1.8-3.3-4.3-5.5-5.3-2.2-1-4.5-.4-4.5 2.1 0 1.2.7 4.1 1.2 5.3.7 1.6 2.5 2.1 4.1 1.8-2 .4-4.4 1.3-4.4 3.9 0 2.5 2.2 3 4.5 2 2.2-1 4.2-3.5 5.5-5.3 1.3 1.8 3.3 4.3 5.5 5.3 2.2 1 4.5.5 4.5-2 0-2.6-2.4-3.5-4.4-3.9 1.6.3 3.4-.2 4.1-1.8.5-1.2 1.2-4.1 1.2-5.3 0-2.5-2.3-3.1-4.5-2.1-2.2 1-4.2 3.5-5.5 5.3Z" />
                    </svg>
                  </a>
                </li>

                {/* Xing */}
                <li className="social-xing">
                  <a href={`https://www.xing.com/spi/shares/new?url=${shareUrl}`} target="_blank" rel="noreferrer">
                    <i className="fa-brands fa-xing"></i>
                  </a>
                </li>

                {/* Email */}
                <li className="social-paper-plane">
                  <a href={`mailto:?subject=${shareTitle}&body=${shareUrl}`}>
                    <i className="fa-solid fa-paper-plane"></i>
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default DetalleNoticiaFabrica;